import React, { useCallback, useEffect, useState } from "react";
import { isEqual } from "lodash";
import { Keyboard, StyleSheet } from "react-native";
import BaseField, { BaseFieldProps } from "./BaseField";
import { SelectChooser, SelectChooserProps } from "./select/SelectChooser";
import { processSelectItems } from "../utils";
import Text from "./Text";
import { useKeyboardVisible } from "../hooks/keyboard";
import { PressableWithFocus } from "./PressableWithFocus";
import { theme } from "../theme";
import { ThemeProvider } from "../contexts/ThemeManager";

type SelectFieldProps<V> = Omit<BaseFieldProps, "component" | "hasValue" | "value"> &
    Omit<SelectChooserProps<V>, "onDismiss" | "visible" | "selectedValue" | "searchItems"> & {
        data: SelectChooserProps<V>["searchItems"];
        value?: SelectChooserProps<V>["selectedValue"];
        searchPlaceholder?: SelectChooserProps<V>["placeholder"];
        placeholder?: string;
        bottomComponent?: React.ReactNode;
        info?: React.ReactNode;
    };

const SelectField = <V,>({
    onSelect: propsOnSelect,
    data,
    value,
    asListAction,
    searchPlaceholder,
    isSelfContained,
    label,
    style,
    error,
    left,
    placeholder,
    right = !left ? (Array.isArray(data) ? "chevron-down" : "search") : undefined,
    onBlur,
    numberOfLines,
    testID,
    bottomComponent,
    halfScreen,
    info,
    ...props
}: SelectFieldProps<V>) => {
    const [isVisible, setIsVisible] = useState(false);
    const isKeyboardVisible = useKeyboardVisible();
    const onSelect = useCallback<NonNullable<SelectChooserProps<V>["onSelect"]>>(
        (item) => {
            void propsOnSelect?.(item);
            setIsVisible(false);
            onBlur?.();
        },
        [propsOnSelect, onBlur],
    );

    const getInitialValue = useCallback(() => {
        if (Array.isArray(data)) {
            return data.find((item) => isEqual(item.value, value) || isEqual(item, value))?.label;
        }
    }, [data, value]);

    const [valueLabel, setValueLabel] = useState(getInitialValue());

    useEffect(() => {
        processSelectItems<V>(data, undefined, (items) => {
            const ret = items.find(
                (item) => isEqual(item.value, value) || isEqual(item, value),
            )?.label;
            if (ret) {
                setValueLabel(ret);
                return;
            } else if (typeof value === "string") {
                setValueLabel(value);
                return;
            }
            setValueLabel("");
        });
    }, [data, value]);

    const onDismiss = useCallback(() => {
        setIsVisible(false);
        onBlur?.();
    }, [onBlur]);

    return (
        <>
            <BaseField
                value={valueLabel}
                asListAction={asListAction}
                label={label}
                placeholder={placeholder}
                right={right}
                left={left}
                style={style}
                error={error}
                disabled={props.disabled}
                numberOfLines={numberOfLines}
                isSelfContained={isSelfContained}
                asButton
                info={info}
                component={
                    <PressableWithFocus
                        testID={testID}
                        disabled={isVisible || props.disabled}
                        onPress={() => {
                            if (Keyboard.isVisible() && halfScreen) {
                                Keyboard.dismiss();
                            }
                            setIsVisible(true);
                        }}
                    />
                }
            />
            <ThemeProvider value={theme}>
                <SelectChooser<V>
                    visible={isVisible && (!halfScreen || (halfScreen && !isKeyboardVisible))}
                    onDismiss={onDismiss}
                    searchItems={data}
                    onSelect={onSelect}
                    placeholder={searchPlaceholder}
                    selectedValue={value}
                    staticTitle={label}
                    bottomComponent={bottomComponent}
                    halfScreen={halfScreen}
                    {...props}
                />
            </ThemeProvider>
        </>
    );
};

export default SelectField;
